import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { STATUS_INCORPORACAO_COR } from './ProdutoIncorporacaoStatusBadge';
import BarraInferiorBotoes from '../../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../components/CustomButton';
import CustomCol from '../../../../../../components/CustomCol';
import { CustomSwal } from '../../../../../../components/CustomFeedback/CustomSwal';
import CustomInputDate from '../../../../../../components/CustomInputs/CustomInputDate';
import CustomInputLink from '../../../../../../components/CustomInputs/CustomInputLink';
import CustomInputText from '../../../../../../components/CustomInputs/CustomInputText';
import CustomOffcanvas from '../../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../../components/CustomRow';
import CustomSelect from '../../../../../../components/CustomSelects/CustomSelect';
import CustomTable from '../../../../../../components/CustomTable';
import Log from '../../../../../../components/Log/Log';
import WorkspaceBotoes from '../../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../../components/WorkspaceFicha';
import { useColor } from '../../../../../../modules/contexts/Color/useColor';
import { useSelect } from '../../../../../../modules/contexts/Select/useSelect';
import {
  IProdutoIncorporacaoForm,
  IProdutoIncorporacaoRegistro,
} from '../../../../../../modules/types/ProdutoDaf';
import { useComponentesPaf } from '../../../../../../services/enum/useComponentesPaf';
import { useGrupoFinanciamento } from '../../../../../../services/enum/useGrupoFinanciamento';
import { useStatusAtualizacao } from '../../../../../../services/enum/useStatusAtualizacao';
import { useStatusIncorporacao } from '../../../../../../services/enum/useStatusIncorporacao';
import { useStatusPactuacao } from '../../../../../../services/enum/useStatusPactuacao';
import { getProdutoDAFIncorporacao } from '../../../../../../services/produtosDAF/produtosDafService';
import { functionLogData } from '../../../../../../utils/functionLogData';
import { formatDateSimp } from '../../../../../../utils/functions';
import { handleDelete } from '../../../../../../utils/handleDelete';
import { handleSave } from '../../../../../../utils/handleSave';
import { headersIncorporacoes } from '../../auxiliarProdutosDaf';
import { defaultProdutoIncorporacaoFormMain } from '../utils/formProdutoIncoporacaoDefault';

interface ProdutoIncorporacoesResumoProps {
  produtoId: string;
  obmId: string;
}

const ProdutoDafIncorporacoes: React.FC<ProdutoIncorporacoesResumoProps> = ({
  produtoId,
  obmId,
}) => {
  const theme = useTheme();
  const { getHexByNome } = useColor();
  const statusIncorporacaoOptions = useStatusIncorporacao();
  const statusAtualizacaoOptions = useStatusAtualizacao();
  const statusPactuacaoOptions = useStatusPactuacao();
  const grupoFinanciamentoOptions = useGrupoFinanciamento();
  const componenteAfOptions = useComponentesPaf();

  const { getSelectDominioPai } = useSelect();
  const componenteAFoptions = getSelectDominioPai(1);
  const grupoFinanciamentoAFoptions = getSelectDominioPai(32);

  // Gatilho para atualização do Log
  const [refreshTriggerLog, setRefreshTriggerLog] = useState(0);
  const triggerRefresh = React.useCallback(() => {
    setRefreshTriggerLog((prev) => prev + 1);
  }, []);

  const [logData, setLogData] = useState<IProdutoIncorporacaoRegistro | null>(
    null,
  );

  const [incorporacoesData, setIncorporacoesData] = useState<any[]>([]);
  const fetchData = React.useCallback(async (id: string | number) => {
    try {
      const response = await getProdutoDAFIncorporacao(id.toString());
      setIncorporacoesData(
        Array.isArray(response)
          ? response.filter((item) => item && item.id != null)
          : [],
      );
    } catch (error) {
      console.error('Erro ao obter dados:', error);
      setIncorporacoesData([]);
    }
  }, []);

  useEffect(() => {
    if (produtoId !== '') {
      fetchData(produtoId);
    }
  }, [produtoId, fetchData]);

  const tableDataIncorporacoes = useMemo(() => {
    if (!Array.isArray(incorporacoesData)) return [];

    return incorporacoesData
      .filter((item): item is Record<string, any> => Boolean(item))
      .map((item, index) => ({
        id: item.id ?? `temp-${produtoId}-${index}`,
        statusIncorporacao:
          statusIncorporacaoOptions[item.statusIncorporacao]?.label ??
          'Não informado',
        statusPactuacao:
          statusPactuacaoOptions[item.statusPactuacao]?.label ??
          'Não informado',
        statusAtualizacao:
          statusAtualizacaoOptions[item.statusAtualizacao]?.label ??
          'Não informado',
        dataIncorporacao: formatDateSimp(item.dataIncorporacao),
        diretrizClinica: item.medsusDiretriz?.nome ?? '—',
        __raw: item,
      }));
  }, [incorporacoesData, statusIncorporacaoOptions, produtoId]);

  //Formulário
  const [dataRaw, setDataRaw] = useState<IProdutoIncorporacaoForm | null>(null);
  const [formMain, setFormMain] = useState<IProdutoIncorporacaoForm>(
    defaultProdutoIncorporacaoFormMain,
  );

  //Controlar offcanvas
  const [show, setShow] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const onNovoRegistro = React.useCallback(() => {
    if (obmId === '') {
      CustomSwal.show({
        type: 'warning',
        title: 'Atenção',
        html: 'Para adicionar incorporações, informe o <br><b>Código OBM</b><br> na identificação do produto.',
        confirmText: 'Entendido',
      });
      return;
    }

    const novoRegistro: IProdutoIncorporacaoForm = {
      ...defaultProdutoIncorporacaoFormMain,
      produtoDAFId: Number(produtoId),
      obmVmpId: Number(obmId),
    };

    setFormMain(novoRegistro);
    setDataRaw(novoRegistro);
    setLogData(null);
    setSaveDisabled(false);
    setShow(true);
  }, [produtoId, obmId]);

  const onClose = React.useCallback(() => {
    setShow(false);
  }, []);

  const handleRowClick = React.useCallback((row: any) => {
    if (!row?.__raw) return;

    const registro = row.__raw as IProdutoIncorporacaoRegistro;

    setFormMain({
      ...defaultProdutoIncorporacaoFormMain,
      ...registro,
    });

    setDataRaw(registro);
    setLogData(registro);
    setSaveDisabled(false);
    setShow(true);
  }, []);

  const handleChange = React.useCallback(
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setFormMain((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  const logs = functionLogData(logData);

  const camposObrigatorios: Record<string, string> = {
    statusIncorporacao: 'Status da Incorporação',
  };

  const camposAjustar = {
    number: [
      'id',
      'usuarioId',
      'statusIncorporacao',
      'statusPactuacao',
      'statusAtualizacao',
      'componentePactuado',
      'componenteGrupoFinanciamento',
      'documentoTecnicoOrientador',
      'produtoDAFId',
      'obmVmpId',
      'medsusDiretrizId',
    ],
    string: [
      'relatorioRecomendacao',
      'portariaIncorporacao',
      'ataPactuacao',
      'motivoAtualizacao',
      'motivoExclusao',
      'observacoes',
    ],
    date: [
      'dataRelatorioRecomendacao',
      'dataDocumento',
      'dataIncorporacao',
      'dataPactuacao',
      'dataDesincorporacao',
      'dataInicioAtualizacao',
      'dataAtualizacao',
    ],
  };

  const salvar = React.useCallback(async () => {
    const ok = await handleSave(
      formMain,
      camposObrigatorios,
      camposAjustar,
      'produtodafincorporacaosus',
      'daf/produtosdaf/produtos/produto',
      undefined,
      false,
      dataRaw,
    );
    if (ok) {
      await fetchData(produtoId);
      setReopenLastAfterSave(true);
      triggerRefresh();
    }
  }, [formMain, camposObrigatorios, camposAjustar, fetchData, dataRaw]);

  const deletar = () =>
    handleDelete(
      formMain.id,
      'produtodafincorporacaosus',
      'daf/produtosdaf/produtos/produto',
    );

  const [statusPactuacaoDisabled, setStatusPactuacaoDisabled] = useState(true);
  const [statusPactuacaoItensDisabled, setStatusPactuacaoItensDisabled] =
    useState(true);
  const [statusAtualizacaoDisabled, setStatusAtualizacaoDisabled] =
    useState(true);
  const [statusAtualizacaoItensDisabled, setStatusAtualizacaoItensDisabled] =
    useState(true);
  const [statusExclusaoDisabled, setStatusExclusaoDisabled] = useState(true);

  useEffect(() => {
    if (formMain.statusIncorporacao > 1) {
      setStatusPactuacaoDisabled(false);
    } else {
      setStatusPactuacaoDisabled(true);
      setStatusPactuacaoItensDisabled(true);

      if (formMain.statusPactuacao !== 0) {
        setFormMain((prev) => ({
          ...prev,
          statusPactuacao: 0,
          dataIncorporacao: '',
          portariaIncorporacao: '',
          linkPortariaIncorporacao: '',
          dataPactuacao: '',
          ataPactuacao: '',
          componentePactuado: 0,
          componenteGrupoFinanciamento: 0,
          ataPactuacaoLink: '',
        }));
      }
    }

    if (formMain.statusPactuacao > 1) {
      setStatusPactuacaoItensDisabled(false);
    } else {
      setStatusPactuacaoItensDisabled(true);

      setFormMain((prev) => ({
        ...prev,
        dataIncorporacao: '',
        portariaIncorporacao: '',
        linkPortariaIncorporacao: '',
        dataPactuacao: '',
        ataPactuacao: '',
        componentePactuado: 0,
        componenteGrupoFinanciamento: 0,
        ataPactuacaoLink: '',
      }));
    }

    if (formMain.statusPactuacao > 1) {
      setStatusAtualizacaoDisabled(false);
    } else {
      setStatusAtualizacaoDisabled(true);
      setStatusAtualizacaoItensDisabled(true);

      if (formMain.statusAtualizacao !== 0) {
        setFormMain((prev) => ({
          ...prev,
          statusAtualizacao: 0,
          dataInicioAtualizacao: '',
          dataAtualizacao: '',
          motivoAtualizacao: '',
        }));
      }
    }

    if (formMain.statusAtualizacao > 1) {
      setStatusAtualizacaoItensDisabled(false);
    } else {
      setStatusAtualizacaoItensDisabled(true);

      setFormMain((prev) => ({
        ...prev,
        dataInicioAtualizacao: '',
        dataAtualizacao: '',
        motivoAtualizacao: '',
      }));
    }

    if (Number(formMain.statusIncorporacao) === 3) {
      setStatusExclusaoDisabled(false);
    } else {
      setStatusExclusaoDisabled(true);

      if (formMain.statusPactuacao !== 0) {
        setFormMain((prev) => ({
          ...prev,
          dataDesincorporacao: '',
          portariaDesincorporacao: '',
          motivoAtualizacao: '',
          linkPortariaDesincorporacao: '',
        }));
      }
    }
  }, [
    formMain.statusIncorporacao,
    formMain.statusPactuacao,
    formMain.statusAtualizacao,
    setFormMain,
  ]);

  const [bgStatusIncorporacao, setBgStatusIncorporacao] = useState<string>();
  const [bgStatusPactuacao, setBgStatusPactuacao] = useState<string>();
  const [bgStatusAtualizacao, setBgStatusAtualizacao] = useState<string>();

  useEffect(() => {
    const resolverBg = (
      value: number | undefined,
      options: Record<number, { label: string }>,
    ) => {
      if (value === undefined || value === null || value === 0) {
        return '#fff';
      }

      const label = options[value]?.label ?? 'Não informado';
      const nomeCor = STATUS_INCORPORACAO_COR[label] ?? 'Status BG - Cinza';

      return getHexByNome(nomeCor, '#fff');
    };

    setBgStatusIncorporacao(
      resolverBg(formMain.statusIncorporacao, statusIncorporacaoOptions),
    );

    setBgStatusPactuacao(
      resolverBg(formMain.statusPactuacao, statusPactuacaoOptions),
    );

    setBgStatusAtualizacao(
      resolverBg(formMain.statusAtualizacao, statusAtualizacaoOptions),
    );
  }, [
    formMain.statusIncorporacao,
    formMain.statusPactuacao,
    formMain.statusAtualizacao,
    statusIncorporacaoOptions,
    statusPactuacaoOptions,
    statusAtualizacaoOptions,
    getHexByNome,
  ]);

  const [reopenLastAfterSave, setReopenLastAfterSave] = useState(false);
  useEffect(() => {
    if (!reopenLastAfterSave) return;
    if (!incorporacoesData.length) return;

    // pressuposto: backend retorna ordenado por criação
    const ultimoRegistro = incorporacoesData[incorporacoesData.length - 1];

    if (!ultimoRegistro?.id) return;

    setFormMain({
      ...defaultProdutoIncorporacaoFormMain,
      ...ultimoRegistro,
    });

    setDataRaw(ultimoRegistro);
    setLogData(ultimoRegistro);

    setShow(true);
    triggerRefresh();

    setReopenLastAfterSave(false);
  }, [incorporacoesData, reopenLastAfterSave]);

  return (
    <>
      <BoxConteudo boxTitulo="Incorporações do Produto na Política de Assistência Farmacêutica">
        <CustomCol width="100%">
          {tableDataIncorporacoes.length > 0 && (
            <CustomTable
              headers={headersIncorporacoes}
              data={tableDataIncorporacoes}
              showPagination={false}
              onRowClick={(row) => handleRowClick(row)}
            />
          )}
        </CustomCol>
        <CustomCol style={{ marginTop: '15px', textAlign: 'right' }}>
          <CustomButton
            label="Novo registro"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={onNovoRegistro}
          />
        </CustomCol>

        <CustomOffcanvas
          title="Incorporação na Política de Assistência Farmacêutica"
          show={show}
          onClose={onClose}
          width={'70vw'}
          placement="end"
        >
          <WorkspaceFicha>
            {/* Dados da Incorporação */}
            <BoxConteudo boxTitulo="Dados da Incorporação">
              <Log
                {...logs}
                entity={'TB_INCORPORACAO_SUS'}
                id={Number(formMain.id)}
                refreshTriggerLog={refreshTriggerLog}
              />
              <BoxConteudo
                boxTitulo="Status de Incorporação"
                style={{ marginTop: '10px' }}
              >
                {/* Status de Incorporação */}
                <CustomRow style={{ marginTop: '-15px' }}>
                  <CustomCol width="200px">
                    <CustomSelect
                      title="Status da Incorporação"
                      name="statusIncorporacao"
                      options={statusIncorporacaoOptions}
                      value={formMain.statusIncorporacao}
                      onChange={handleChange}
                      required
                      style={{
                        backgroundColor: bgStatusIncorporacao,
                      }}
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomSelect
                      title="Status da Pactuação"
                      name="statusPactuacao"
                      options={statusPactuacaoOptions}
                      value={formMain.statusPactuacao}
                      onChange={handleChange}
                      disabled={statusPactuacaoDisabled}
                      style={
                        statusPactuacaoDisabled
                          ? undefined
                          : { backgroundColor: bgStatusPactuacao }
                      }
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomSelect
                      title="Status de Atualização"
                      name="statusAtualizacao"
                      options={statusAtualizacaoOptions}
                      value={formMain.statusAtualizacao}
                      onChange={handleChange}
                      disabled={statusAtualizacaoDisabled}
                      style={
                        statusAtualizacaoDisabled
                          ? undefined
                          : { backgroundColor: bgStatusAtualizacao }
                      }
                    />
                  </CustomCol>
                  <CustomCol></CustomCol>
                </CustomRow>
              </BoxConteudo>

              {/* Relatório de Recomendação */}
              <BoxConteudo
                boxTitulo="Relatório de Recomendação"
                style={{ marginTop: '10px' }}
                fechado
              >
                <CustomRow>
                  <CustomCol width="200px">
                    <CustomInputDate
                      title="Data do Relatório"
                      name="dataRelatorioRecomendacao"
                      value={formMain.dataRelatorioRecomendacao}
                      onChange={handleChange}
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomInputText
                      title="Relatório de Recomendação"
                      name="relatorioRecomendacao"
                      value={formMain.relatorioRecomendacao}
                      onChange={handleChange}
                    />
                  </CustomCol>
                </CustomRow>
                <CustomRow>
                  <CustomCol>
                    <CustomInputLink
                      title="Link do Relatório"
                      name="linkRelatorioRecomendacao"
                      value={formMain.linkRelatorioRecomendacao}
                      onChange={handleChange}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              {/* Incorporação */}
              <BoxConteudo
                boxTitulo="Incorporação"
                style={{ marginTop: '10px' }}
                fechado
              >
                <CustomRow>
                  <CustomCol width="200px">
                    <CustomInputDate
                      title="Data da Incorporação"
                      name="dataIncorporacao"
                      value={formMain.dataIncorporacao}
                      onChange={handleChange}
                      readOnly={statusPactuacaoDisabled}
                      minDate={
                        formMain.dataRelatorioRecomendacao
                          ? new Date(formMain.dataRelatorioRecomendacao)
                          : undefined
                      }
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomInputText
                      title="Nº da Portaria de Incorporação"
                      name="portariaIncorporacao"
                      value={formMain.portariaIncorporacao}
                      onChange={handleChange}
                      readOnly={statusPactuacaoDisabled}
                    />
                  </CustomCol>
                </CustomRow>
                <CustomRow>
                  <CustomCol>
                    <CustomInputLink
                      title="Link da Portaria de Incorporação"
                      name="linkPortariaIncorporacao"
                      value={formMain.linkPortariaIncorporacao}
                      onChange={handleChange}
                      readOnly={statusPactuacaoDisabled}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              {/* Pactuação */}
              <BoxConteudo
                boxTitulo="Pactuação"
                style={{ marginTop: '10px' }}
                fechado
              >
                <CustomRow>
                  <CustomCol width="200px" helpText="Data da reunião da CIT">
                    <CustomInputDate
                      title="Data da Pactuação"
                      name="dataPactuacao"
                      value={formMain.dataPactuacao}
                      onChange={handleChange}
                      readOnly={statusPactuacaoItensDisabled}
                      minDate={
                        formMain.dataIncorporacao
                          ? new Date(formMain.dataIncorporacao)
                          : undefined
                      }
                    />
                  </CustomCol>
                  <CustomCol>
                    <CustomInputText
                      title="Nº da Ata"
                      name="ataPactuacao"
                      value={formMain.ataPactuacao}
                      onChange={handleChange}
                      readOnly={statusPactuacaoItensDisabled}
                    />
                  </CustomCol>

                  <CustomCol width="200px">
                    <CustomSelect
                      title="Componente AF"
                      name="componentePactuado"
                      options={componenteAFoptions}
                      value={formMain.componentePactuado}
                      onChange={handleChange}
                      disabled={statusPactuacaoItensDisabled}
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomSelect
                      title="Grupo de Financiamento"
                      name="componenteGrupoFinanciamento"
                      options={grupoFinanciamentoAFoptions}
                      value={formMain.componenteGrupoFinanciamento}
                      onChange={handleChange}
                      disabled={statusPactuacaoItensDisabled}
                    />
                  </CustomCol>
                </CustomRow>
                <CustomRow>
                  <CustomCol>
                    <CustomInputLink
                      title="Link da Ata"
                      name="ataPactuacaoLink"
                      value={formMain.ataPactuacaoLink}
                      onChange={handleChange}
                      readOnly={statusPactuacaoItensDisabled}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              {/* Status Atualização */}
              <BoxConteudo
                boxTitulo="Atualização"
                style={{ marginTop: '10px' }}
                fechado
              >
                <CustomRow>
                  <CustomCol width="200px">
                    <CustomInputDate
                      title="Data de Início Atualização"
                      name="dataInicioAtualizacao"
                      value={formMain.dataInicioAtualizacao}
                      onChange={handleChange}
                      readOnly={statusAtualizacaoItensDisabled}
                      minDate={
                        formMain.dataPactuacao
                          ? new Date(formMain.dataPactuacao)
                          : undefined
                      }
                    />
                  </CustomCol>
                  <CustomCol width="200px">
                    <CustomInputDate
                      title="Data da Atualização"
                      name="dataAtualizacao"
                      value={formMain.dataAtualizacao}
                      onChange={handleChange}
                      readOnly={statusAtualizacaoItensDisabled}
                      minDate={
                        formMain.dataInicioAtualizacao
                          ? new Date(formMain.dataInicioAtualizacao)
                          : undefined
                      }
                    />
                  </CustomCol>
                  <CustomCol></CustomCol>
                </CustomRow>
                <CustomRow>
                  <CustomCol>
                    <CustomInputText
                      title="Motivo da atualização"
                      name="motivoAtualizacao"
                      value={formMain.motivoAtualizacao || ''}
                      onChange={handleChange}
                      type="textArea"
                      rows={3}
                      readOnly={statusAtualizacaoItensDisabled}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              {/* Desincorporação */}
              <BoxConteudo
                boxTitulo="Exclusão"
                style={{ marginTop: '10px' }}
                fechado
              >
                <CustomRow>
                  <CustomCol width="200px">
                    <CustomInputDate
                      title="Data da Exclusão"
                      name="dataDesincorporacao"
                      value={formMain.dataDesincorporacao}
                      onChange={handleChange}
                      readOnly={statusExclusaoDisabled}
                      minDate={
                        formMain.dataAtualizacao
                          ? new Date(formMain.dataAtualizacao)
                          : undefined
                      }
                    />
                  </CustomCol>

                  <CustomCol width="300px">
                    <CustomInputText
                      title="Nº da Portaria de Desincorporação"
                      name="portariaDesincorporacao"
                      value={formMain.portariaDesincorporacao}
                      onChange={handleChange}
                      readOnly={statusExclusaoDisabled}
                    />
                  </CustomCol>
                  <CustomCol></CustomCol>
                </CustomRow>

                <CustomRow>
                  <CustomCol>
                    <CustomInputText
                      title="Motivo da exclusão"
                      name="motivoExclusao"
                      value={formMain.motivoExclusao || ''}
                      onChange={handleChange}
                      type="textArea"
                      rows={3}
                      readOnly={statusExclusaoDisabled}
                    />
                  </CustomCol>
                </CustomRow>

                <CustomRow>
                  <CustomCol>
                    <CustomInputLink
                      name="linkPortariaDesincorporacao"
                      title="Link da Portaria de Desincorporação"
                      value={formMain.linkPortariaDesincorporacao}
                      onChange={handleChange}
                      readOnly={statusExclusaoDisabled}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>

              {/* Observações */}
              <BoxConteudo
                boxTitulo="Observações"
                style={{ marginTop: '10px' }}
                fechado
              >
                <CustomRow>
                  <CustomCol width="100%">
                    <CustomInputText
                      title=""
                      name="observacoes"
                      value={formMain.observacoes || 'Sem observações.'}
                      type="textArea"
                      onChange={handleChange}
                      rows={4}
                    />
                  </CustomCol>
                </CustomRow>
              </BoxConteudo>
              <BarraInferiorBotoes>
                <WorkspaceBotoes
                  save
                  delete={formMain.id ? true : false}
                  onClickDelete={formMain.id ? deletar : undefined}
                  saveDisabled={saveDisabled}
                  onClickSave={salvar}
                />
              </BarraInferiorBotoes>
            </BoxConteudo>
          </WorkspaceFicha>
        </CustomOffcanvas>
      </BoxConteudo>
    </>
  );
};

export default ProdutoDafIncorporacoes;
